// Backend · Alerts: which monitoring modes a rule can take, and why not.
//
// Pure helpers shared by the Rules tab, the rule editor and the protected-rule
// drawer. Capability comes from the server-computed catalog flags
// (`supports_campaign_running_production` / `_pixel`), the exact predicate the
// validator enforces, so this screen never re-derives it.

import type { AlertActivationMode, AlertMetricCatalogEntry } from '@/api/types';

export type ActivationModeId = AlertActivationMode;

export interface ActivationOption {
  id: ActivationModeId;
  label: string;
  unavailable?: boolean;
  unavailableReason?: string;
}

export interface ActivationPixel {
  id: string;
  default_production_id?: string | null;
}

export interface ActivationOptionContext {
  currentMode?: string | null;
  pixels?: readonly ActivationPixel[];
  pixelsLoaded?: boolean;
}

/** The scope fields the activation helpers read; every `AlertScope` arm fits. */
export interface ActivationScope {
  level: string;
  production_id?: string | null;
  campaign_id?: string | null;
  pixel_id?: string | null;
}

/**
 * The rule fields the activation helpers read. A saved `AlertRule` fits, and so
 * does the editor's draft, which carries only the trigger type and metric.
 */
export interface ActivationRule {
  activation_mode?: ActivationModeId | null;
  enabled?: boolean;
  scope?: ActivationScope | null;
  trigger?: { type: string; metric?: string } | null;
}

/** The catalog fields the activation helpers read. */
export type ActivationCatalogEntry = Pick<
  AlertMetricCatalogEntry,
  'available' | 'supports_campaign_running_production' | 'supports_campaign_running_pixel'
> & { metric: string };

export type CampaignRunningCapability = 'supported' | 'unsupported' | 'unknown';

export const ACTIVATION_OPTIONS: readonly ActivationOption[] = [
  { id: 'off', label: 'Off' },
  { id: 'always', label: 'Always' },
  { id: 'campaign_running', label: 'Campaign running' },
];

export function activationModeOf(rule: Pick<ActivationRule, 'activation_mode' | 'enabled'>): ActivationModeId {
  // The fallback keeps a rolling frontend deploy honest while the additive
  // wire field propagates through cached responses.
  return rule?.activation_mode ?? (rule?.enabled === false ? 'off' : 'always');
}

export function campaignRunningCapability(
  rule: ActivationRule,
  metricCatalog: readonly ActivationCatalogEntry[] = [],
  pixels: readonly ActivationPixel[] = [],
  pixelsLoaded = true,
): CampaignRunningCapability {
  const triggerType = rule?.trigger?.type;
  if (triggerType !== 'metric' && triggerType !== 'system_metric') return 'unsupported';
  const metric = metricCatalog.find((entry) => entry.metric === rule.trigger?.metric);
  if (!metric) return 'unsupported';
  // Server-computed capability flags — the exact predicate the validator
  // enforces (shared::AlertTrigger::supports_campaign_running_scope), already
  // folding in availability — so this screen can never drift from the backend.
  if (rule?.scope?.level === 'production') {
    return metric.supports_campaign_running_production ? 'supported' : 'unsupported';
  }
  if (rule?.scope?.level === 'pixel') {
    if (!metric.supports_campaign_running_pixel) return 'unsupported';
    const pixelId = rule.scope.pixel_id;
    if (!pixelId) return 'supported';
    if (!pixelsLoaded) return 'unknown';
    const pixel = pixels.find((candidate) => candidate.id === pixelId);
    return pixel?.default_production_id ? 'supported' : 'unsupported';
  }
  return 'unsupported';
}

/**
 * Why Campaign running cannot be chosen for this rule, or `null` when it can.
 * One sentence, the same in the option's tooltip and in the visible line the
 * Rules tab shows under the selector.
 */
export function campaignRunningUnavailableReason(
  rule: ActivationRule,
  metricCatalog: readonly ActivationCatalogEntry[] = [],
  {
    pixels = [],
    pixelsLoaded = true,
  }: ActivationOptionContext = {},
): string | null {
  const capability = campaignRunningCapability(rule, metricCatalog, pixels, pixelsLoaded);
  if (capability === 'supported') return null;
  if (capability === 'unknown') {
    return 'Checking this pixel’s default-production mapping before enabling Campaign running.';
  }
  const triggerType = rule?.trigger?.type;
  const metric = (triggerType === 'metric' || triggerType === 'system_metric')
    ? metricCatalog.find((entry) => entry.metric === rule.trigger?.metric)
    : null;
  return triggerType !== 'metric' && triggerType !== 'system_metric'
    ? 'Campaign running is unavailable for log and job triggers.'
    : !metric?.available
      ? 'Campaign running is unavailable because this metric is not currently evaluable.'
      : rule?.scope?.level === 'global'
        ? 'Campaign running is unavailable for global scope.'
        : rule?.scope?.level === 'campaign'
          ? 'Campaign running is unavailable for campaign scope because this evaluator does not emit campaign-level findings.'
          : rule?.scope?.level === 'production'
            ? 'Campaign running is unavailable because this metric does not emit production-level findings.'
            : rule?.scope?.level === 'pixel'
              ? 'Campaign running is unavailable because this pixel is not mapped to a production.'
              : 'Campaign running is unavailable because this is not a pixel metric.';
}

export function activationOptionsForRule(
  rule: ActivationRule,
  metricCatalog: readonly ActivationCatalogEntry[] = [],
  context: ActivationOptionContext = {},
): readonly ActivationOption[] {
  const reason = campaignRunningUnavailableReason(rule, metricCatalog, context);
  if (reason === null) return ACTIVATION_OPTIONS;
  // Keep the option visible — dimmed and struck out, with the reason in its
  // tooltip and, on the Rules tab, in a line under the selector — but never
  // offer it as an action. The radiogroup keeps a stable shape
  // mid-interaction, so arrowing away from an unsupported saved mode does not
  // make a button vanish under the operator (or under a screen reader).
  return [
    ...ACTIVATION_OPTIONS.slice(0, 2),
    {
      id: 'campaign_running',
      label: 'Campaign running',
      unavailable: true,
      unavailableReason: reason,
    },
  ];
}

export function activationHelpForRule(
  rule: ActivationRule,
  metricCatalog: readonly ActivationCatalogEntry[] = [],
  {
    currentMode = null,
    pixels = [],
    pixelsLoaded = true,
  }: ActivationOptionContext = {},
): string {
  const capability = campaignRunningCapability(rule, metricCatalog, pixels, pixelsLoaded);
  if (capability === 'supported' && rule?.scope?.level === 'production') {
    return rule.scope.production_id
      ? 'Campaign running follows this production and waits whenever none of its campaigns is running.'
      : 'Each production is monitored only while one of its own campaigns is running.';
  }
  if (capability === 'supported' && rule?.scope?.level === 'pixel') {
    return rule.scope.pixel_id
      ? 'This pixel is monitored only while its default production has a running campaign. A pixel without a default production remains waiting.'
      : 'Each active pixel is monitored only while its default production has a running campaign. Pixels without a default production remain waiting.';
  }
  const reason = campaignRunningUnavailableReason(rule, metricCatalog, { pixels, pixelsLoaded })
    // Supported, but for neither a production nor a pixel scope.
    ?? 'Campaign running is unavailable because this is not a pixel metric.';
  if (capability === 'unknown') return reason;
  return currentMode === 'campaign_running'
    ? `${reason} This saved mode is shown for safety; choose Off or Always to move the rule to a supported mode.`
    : reason;
}
